import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Alert, Box, Button, Chip, CircularProgress, IconButton, Typography} from '@mui/material';
import {Fullscreen, FullscreenExit, PlayArrow, Stop} from '@mui/icons-material';

/**
 * Kid MJPEG Player Component
 * Simplified MJPEG video player for kid streaming with token-based authentication
 * Handles loading, error states, and fullscreen automatically
 */
const KidMJPEGPlayer = ({
                            streamUrl,
                            childName = '',
                            cameraInfo = '',
                            onError,
                            onLoad,
                            onStop,
                            showControls = true,
                            showLoading = true,
                            style = {},
                            className = '',
                            ...props
                        }) => {
    // State management
    const [isStreaming, setIsStreaming] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [errorRetryCount, setErrorRetryCount] = useState(0);

    // Refs
    const imgRef = useRef(null);
    const containerRef = useRef(null);
    const errorTimeoutRef = useRef(null);

    // Handle image load
    const handleImageLoad = useCallback(() => {
        console.log('KidMJPEGPlayer: Stream loaded successfully');

        // Clear any pending error timeout
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = null;
        }

        // Reset error retry count
        setErrorRetryCount(0);
        setError(null);
        setIsStreaming(true);
        setIsConnecting(false);

        if (onLoad) {
            onLoad();
        }
    }, [onLoad]);

    // Handle image error
    const handleImageError = useCallback((e) => {
        console.error('KidMJPEGPlayer: Image stream error:', e);

        // Clear any pending error timeout
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
        }

        // If stream was already established, show error immediately
        if (isStreaming) {
            const errorMsg = 'Stream connection interrupted. Please check if the camera is accessible and the Python service is running.';
            setError(errorMsg);
            setIsStreaming(false);
            setIsConnecting(false);
            if (onError) {
                onError(new Error(errorMsg));
            }
            return;
        }

        // If still connecting, wait a bit before showing error
        const newRetryCount = errorRetryCount + 1;
        setErrorRetryCount(newRetryCount);

        if (newRetryCount >= 3) {
            const errorMsg = 'Stream connection error. Please check if the camera is accessible and the Python service is running.';
            setError(errorMsg);
            setIsConnecting(false);
            if (onError) {
                onError(new Error(errorMsg));
            }
        } else {
            // Wait before showing error - might recover
            errorTimeoutRef.current = setTimeout(() => {
                if (!isStreaming && isConnecting) {
                    const errorMsg = 'Stream connection timeout. Please check if the camera is accessible and the Python service is running.';
                    setError(errorMsg);
                    setIsConnecting(false);
                    if (onError) {
                        onError(new Error(errorMsg));
                    }
                }
            }, 5000);
        }
    }, [isStreaming, isConnecting, errorRetryCount, onError]);

    // Handle fullscreen toggle
    const handleFullscreenToggle = useCallback(() => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen().catch(err => {
                console.error('Error attempting to enable fullscreen:', err);
            });
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    }, []);

    // Handle fullscreen change
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    // Set stream URL when it changes
    useEffect(() => {
        if (streamUrl) {
            setIsConnecting(true);
            setError(null);
            setErrorRetryCount(0);

            // Clear any pending error timeout
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
                errorTimeoutRef.current = null;
            }

            // Small delay to ensure image element is ready
            const timeout = setTimeout(() => {
                if (imgRef.current) {
                    console.log('KidMJPEGPlayer: Setting stream URL:', streamUrl);
                    imgRef.current.src = '';
                    setTimeout(() => {
                        if (imgRef.current) {
                            imgRef.current.src = streamUrl;
                        }
                    }, 50);
                }
            }, 100);

            return () => clearTimeout(timeout);
        } else {
            // Clear stream if URL is removed
            if (imgRef.current) {
                imgRef.current.src = '';
            }
            setIsStreaming(false);
            setIsConnecting(false);
        }
    }, [streamUrl]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }
            if (imgRef.current) {
                imgRef.current.src = '';
            }
        };
    }, []);

    // Handle stop
    const handleStop = useCallback(() => {
        if (imgRef.current) {
            imgRef.current.src = '';
        }
        setIsStreaming(false);
        setIsConnecting(false);
        setError(null);
        setErrorRetryCount(0);

        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = null;
        }

        if (onStop) {
            onStop();
        }
    }, [onStop]);

    return (
        <Box
            ref={containerRef}
            style={{
                position: 'relative',
                backgroundColor: '#000',
                borderRadius: '8px',
                overflow: 'hidden',
                minHeight: '300px',
                ...style
            }}
            className={className}
            {...props}
        >
            {/* Loading Indicator */}
            {(isConnecting || (!isStreaming && streamUrl)) && showLoading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0, 0, 0, 0.7)',
                        zIndex: 2,
                        backdropFilter: 'blur(4px)'
                    }}
                >
                    <CircularProgress sx={{mb: 2}} color="primary" size={60}/>
                    <Typography variant="h6" color="white" sx={{mb: 1}}>
                        {isConnecting ? 'Connecting to stream...' : 'Buffering stream...'}
                    </Typography>
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.8)" align="center">
                        {isConnecting
                            ? `Establishing connection${childName ? ` for ${childName}` : ''}`
                            : 'Fetching video buffer from Python service...'}
                    </Typography>
                    {cameraInfo && (
                        <Typography variant="caption" color="rgba(255, 255, 255, 0.6)" sx={{mt: 1}}>
                            {cameraInfo}
                        </Typography>
                    )}
                </Box>
            )}

            {/* Video Display */}
            <img
                ref={imgRef}
                alt="Face-Masked MJPEG Stream"
                onError={handleImageError}
                onLoad={handleImageLoad}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                    opacity: (isConnecting || (!isStreaming && streamUrl)) ? 0.3 : 1,
                    transition: 'opacity 0.3s',
                    minHeight: '300px'
                }}
            />

            {/* Status Overlay */}
            {isStreaming && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        zIndex: 1
                    }}
                >
                    <Chip
                        label="LIVE"
                        color="success"
                        icon={<PlayArrow/>}
                        size="small"
                    />
                    {childName && (
                        <Chip
                            label={childName}
                            color="primary"
                            variant="outlined"
                            size="small"
                        />
                    )}
                    {cameraInfo && (
                        <Chip
                            label={cameraInfo}
                            color="info"
                            variant="outlined"
                            size="small"
                        />
                    )}
                </Box>
            )}

            {/* Controls */}
            {showControls && isStreaming && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                        gap: 1,
                        zIndex: 1
                    }}
                >
                    <IconButton
                        onClick={handleFullscreenToggle}
                        sx={{
                            bgcolor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.9)'
                            }
                        }}
                        size="small"
                        title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                    >
                        {isFullscreen ? <FullscreenExit/> : <Fullscreen/>}
                    </IconButton>
                    {onStop && (
                        <IconButton
                            onClick={handleStop}
                            sx={{
                                bgcolor: 'rgba(211, 47, 47, 0.7)',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'rgba(211, 47, 47, 0.9)'
                                }
                            }}
                            size="small"
                            title="Stop stream"
                        >
                            <Stop/>
                        </IconButton>
                    )}
                </Box>
            )}

            {/* Error Display */}
            {error && (
                <Alert
                    severity="error"
                    sx={{
                        position: 'absolute',
                        bottom: 16,
                        left: 16,
                        right: 16,
                        zIndex: 3
                    }}
                    action={
                        <Button
                            size="small"
                            color="inherit"
                            onClick={() => {
                                setError(null);
                                setErrorRetryCount(0);
                                if (streamUrl && imgRef.current) {
                                    imgRef.current.src = '';
                                    setTimeout(() => {
                                        if (imgRef.current) {
                                            imgRef.current.src = streamUrl;
                                        }
                                    }, 100);
                                }
                            }}
                        >
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
            )}
        </Box>
    );
};

KidMJPEGPlayer.propTypes = {
    streamUrl: PropTypes.string.isRequired,
    childName: PropTypes.string,
    cameraInfo: PropTypes.string,
    onError: PropTypes.func,
    onLoad: PropTypes.func,
    onStop: PropTypes.func,
    showControls: PropTypes.bool,
    showLoading: PropTypes.bool,
    style: PropTypes.object,
    className: PropTypes.string
};

export default KidMJPEGPlayer;

